import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { getUserLocationInfo, saveUserDetailsFormData, getUserDetails, getFormDetails } from '../api';
import { useAuth } from '../hooks';

export const useUserLocation = () => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['user-location'],
    queryFn: getUserLocationInfo,
    staleTime: Infinity,
    retry: 0,
    onError: (error) => {
      console.error('Error fetching user location:', error);
      queryClient.setQueryData(['user-location'], null);
    }
  });
};

export const useSaveUserDetails = () => {
  const queryClient = useQueryClient();
  const { token } = useAuth();

  return useMutation({
    mutationFn: (formData) => saveUserDetailsFormData({ formData, token }),
    onSuccess: (data, variables) => {
      // Invalidate the user details query to trigger a refresh
      queryClient.invalidateQueries(['user-details', variables._id, variables.instituteId]);
    },
    onError: (error) => {
      console.error('Error saving user details:', error);
    }
  });
};

// Hook to fetch user details directly from the user profile
export const useUserDetails = () => {
  const { token } = useAuth();
  
  return useQuery({
    queryKey: ['user-profile-details'],
    queryFn: () => getUserDetails({ token }),
    enabled: !!token,
    staleTime: 1000 * 60 * 5, // 5 minutes
    onError: (error) => {
      console.error('Error fetching user profile details:', error);
    }
  });
};

// Hook to fetch form details by formId
export const useFormDetails = (formId) => {
  const { token } = useAuth();
  
  return useQuery({
    queryKey: ['form-details', formId],
    queryFn: () => getFormDetails({ formId, token }),
    enabled: !!formId && !!token,
    staleTime: 1000 * 60 * 60, // 1 hour
    onError: (error) => {
      console.error('Error fetching form details:', error);
    }
  });
};
